import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { ApiService } from '../services/api';
import { Store } from '../types';

interface StoreLookup {
  byStoreId: Record<number, string>;
  byChainId: Record<number, string>; // first store per chain (insertion order)
}

const EMPTY_LOOKUP: StoreLookup = { byStoreId: {}, byChainId: {} };

let cachedLookupPromise: Promise<StoreLookup> | null = null;
let cachedToken: string | null = null;

function buildLookup(stores: Store[]): StoreLookup {
  const byStoreId: Record<number, string> = {};
  const byChainId: Record<number, string> = {};
  for (const store of stores) {
    byStoreId[store.storeId] = store.timeZone;
    if (byChainId[store.chainId] === undefined) {
      byChainId[store.chainId] = store.timeZone;
    }
  }
  return { byStoreId, byChainId };
}

function loadLookup(token: string): Promise<StoreLookup> {
  if (cachedLookupPromise && cachedToken === token) return cachedLookupPromise;
  cachedToken = token;
  cachedLookupPromise = ApiService.fetchStores(token)
    .then(buildLookup)
    .catch((err) => {
      cachedLookupPromise = null;
      cachedToken = null;
      console.error('Failed to load stores for timezone formatting:', err);
      return EMPTY_LOOKUP;
    });
  return cachedLookupPromise;
}

/**
 * Look up store timezones with chain fallback.
 *
 * Returns a stable `getTimeZone(storeId?, chainId?)` function that:
 *  - resolves by storeId first
 *  - falls back to the first store in the same chainId
 *  - reads from a ref, so frozen closures (DataTable column renders) get
 *    live data once the fetch resolves.
 *
 * Also returns `isLoaded` (state-backed) so single-store screens that read
 * the tz inline can re-render once data is available.
 */
export function useStoreTimeZones() {
  const { token } = useContext(AuthContext);
  const lookupRef = useRef<StoreLookup>(EMPTY_LOOKUP);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    loadLookup(token).then((lookup) => {
      if (cancelled) return;
      lookupRef.current = lookup;
      setIsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const getTimeZone = useCallback(
    (storeId?: number | null, chainId?: number | null): string | undefined => {
      if (storeId != null) {
        const tz = lookupRef.current.byStoreId[storeId];
        if (tz) return tz;
      }
      if (chainId != null) {
        return lookupRef.current.byChainId[chainId];
      }
      return undefined;
    },
    // isLoaded isn't read inside the function, but we want it as a dep: it
    // forces a fresh function identity once stores load so consumers reading
    // the tz inline (e.g. `const tz = getTimeZone(storeId)`) re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoaded]
  );

  return { getTimeZone };
}
